import { useEffect, useMemo } from 'react';
import { ThemeCSSVariables } from '@asset-metrix/portal-gateway-client-js';
import { useFavicon } from '@mantine/hooks';
import convert from 'color-convert';
import { scheme } from 'vega';

import '../i18n/config';

import { getFavIconUrl } from '../utils/getFavIconUrl';
import { BASE_FONT_SIZE } from '../utils/pixelSizeHelperPortalUi';

type RGB = [number, number, number];

const THEME_CSS_VARIABLES: (keyof ThemeCSSVariables)[] = [
  '--portal-ui-color-primary',
  '--portal-ui-color-secondary',
  '--portal-ui-font-family',
  '--portal-ui-logo',
  '--portal-ui-logo-small',
  '--portal-ui-background-image',
  '--portal-ui-favicon',
  '--portal-ui-logo-footer',
];

export function usePortalTheme(
  colorScheme: 'light' | 'dark' = 'light',
  themes?: { light: ThemeCSSVariables; dark: ThemeCSSVariables } | null,
  container = document.body
) {
  const theme: ThemeCSSVariables | undefined = useMemo(
    () => themes?.[colorScheme],
    [colorScheme, themes]
  );

  useFavicon(getFavIconUrl(theme?.['--portal-ui-favicon']));

  useEffect(() => {
    if (container) {
      const schemeClass = `portal-theme--${colorScheme}`;
      const otherSchemeClass = `portal-theme--${
        colorScheme === 'dark' ? 'light' : 'dark'
      }`;

      container.classList.toggle('portal-theme', true);
      container.classList.toggle(schemeClass, true);
      container.classList.remove(otherSchemeClass);

      for (const cssVar of THEME_CSS_VARIABLES) {
        const value = theme?.[cssVar];
        if (value && typeof value === 'string') {
          container.style.setProperty(cssVar, value);
        } else {
          container.style.removeProperty(cssVar);
        }
      }

      document.body.style.setProperty(
        '--portal-ui-base-font-size',
        `${BASE_FONT_SIZE}px`
      );

      document.body.classList.toggle(
        'with-bg-logo',
        !!theme?.['--portal-ui-logo-footer']
      );
    }

    if (theme) {
      // Register color schemes
      const { '--portal-ui-color-primary': primaryColor } = theme;
      const primaryColorRgb = primaryColor.split(',') as unknown as RGB;
      const [primaryColorH, primaryColorS, primaryColorL] =
        convert.rgb.hsl(primaryColorRgb);

      const colorsFull: string[] = theme?.['vega-chart-color-schema'] || [];
      const gradientColors: string[] =
        theme?.['vega-chart-gradient-colors'] || [];

      scheme(
        'AssetMetrix12',
        colorsFull.filter((_c, idx) => idx % 2 === 0)
      );

      scheme('AssetMetrix24', colorsFull);

      const singleHueBlack = (f: number) => {
        const b = 10;
        const a = primaryColorL - b;

        const H = primaryColorH;
        const S = primaryColorS;
        const L = Math.max(0, Math.min(100, a * f + b));
        return `#${convert.hsl.hex([H, S, L])}`;
      };

      const singleHueWhite = (f: number) => {
        const b = 90;
        const a = primaryColorL - b;

        const H = primaryColorH;
        const S = primaryColorS;
        const L = Math.max(0, Math.min(100, a * f + b));
        return `#${convert.hsl.hex([H, S, L])}`;
      };

      scheme('AssetMetrixSingleHueBlack', singleHueBlack);
      scheme('AssetMetrixSingleHueWhite', singleHueWhite);

      const multiHueColorSchemeGenerator = (inputValue: number) => {
        const numColors = gradientColors.length;
        const clamp = (a: number) => Math.min(numColors - 1, Math.max(a, 0));

        const scaledInput = inputValue * (numColors - 1);
        const lowerIndex = clamp(Math.floor(scaledInput));
        const upperIndex = clamp(Math.ceil(scaledInput));
        const lowerColor = convert.hex.rgb(gradientColors[lowerIndex]);
        const upperColor = convert.hex.rgb(gradientColors[upperIndex]);
        const weight = scaledInput - lowerIndex;

        // Linear interpolation between the two colors
        const r = lowerColor[0] + (upperColor[0] - lowerColor[0]) * weight;
        const g = lowerColor[1] + (upperColor[1] - lowerColor[1]) * weight;
        const b = lowerColor[2] + (upperColor[2] - lowerColor[2]) * weight;

        return `#${convert.rgb.hex([r, g, b])}`;
      };

      scheme('multiHueColorScheme', multiHueColorSchemeGenerator);
    }
  }, [colorScheme, theme, container]);
}
